import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { notificationService, type NotificationItem } from '../../services/notifications';
import { useAuth } from '../../hooks/useAuth';
import { localStorageService } from '../../services/localStorage';
import FeedItemCard from './FeedItemCard';

const FEED_CACHE_KEY = 'feed_notifications_cache';
const FEED_CACHE_TTL = 5 * 60 * 1000;

interface FeedCache {
  items: NotificationItem[];
  nextBefore: string | null;
  hasMore: boolean;
}

export default function Feed() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Initial load
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchFeed = async () => {
      const cached = localStorageService.get<FeedCache>(FEED_CACHE_KEY);
      if (cached) {
        setItems(cached.items);
        setNextBefore(cached.nextBefore);
        setHasMore(cached.hasMore);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await notificationService.getNotifications();
        setItems(response.data);
        setNextBefore(response.meta.next_before);
        setHasMore(response.meta.has_more);
        localStorageService.set<FeedCache>(FEED_CACHE_KEY, {
          items: response.data,
          nextBefore: response.meta.next_before,
          hasMore: response.meta.has_more,
        }, FEED_CACHE_TTL);
      } catch (err) {
        console.error('Failed to fetch feed:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchFeed();
  }, [isAuthenticated]);

  // Load more items
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !nextBefore) return;

    try {
      setLoadingMore(true);
      const response = await notificationService.getNotifications(nextBefore);
      setItems(prev => [...prev, ...response.data]);
      setNextBefore(response.meta.next_before);
      setHasMore(response.meta.has_more);
    } catch (err) {
      console.error('Failed to load more feed items:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, nextBefore]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadMore]);

  const handleItemClick = (item: NotificationItem) => {
    const url = item.attributes.url;
    if (url) {
      // Navigate to the action URL (stored in params, absolute frontend URL)
      const path = url.replace(/^https?:\/\/[^/]+/, '');
      window.location.href = path || url;
    }
    // Mark as viewed in the background
    notificationService.markViewed([item.id]).catch(() => {});
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="w-full max-w-3xl mx-auto space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i}>
            <CardContent className="p-3">
              <div className="flex gap-3">
                <div className="w-16 h-16 bg-muted animate-pulse rounded-md flex-shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
                    <div className="h-3 bg-muted animate-pulse rounded w-12 flex-shrink-0" />
                  </div>
                  <div className="h-3 bg-muted animate-pulse rounded w-1/2" />
                  <div className="h-3 bg-muted animate-pulse rounded w-24" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-3xl mx-auto">
        <Card>
          <CardContent className="p-4 text-center text-muted-foreground">
            {t('feed.error')}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="w-full max-w-3xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {t('feed.empty')}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto space-y-4">
      {items.map((item) => (
        <FeedItemCard
          key={item.id}
          item={item}
          unread={!item.attributes.viewed}
          onClick={() => handleItemClick(item)}
        />
      ))}

      {hasMore && (
        <div ref={loadMoreRef} className="py-4">
          {loadingMore && (
            <Card>
              <CardContent className="p-3">
                <div className="flex gap-3">
                  <div className="w-16 h-16 bg-muted animate-pulse rounded-md flex-shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
                      <div className="h-3 bg-muted animate-pulse rounded w-12 flex-shrink-0" />
                    </div>
                    <div className="h-3 bg-muted animate-pulse rounded w-1/2" />
                    <div className="h-3 bg-muted animate-pulse rounded w-24" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
